import { useEffect, useRef, useState } from 'react';

const CYCLING_INTERVAL_MS = 900;

/**
 * Cycles through an array of icon URLs every 900ms, returning the current one.
 * Only runs the interval when `isActive` is true — stops cycling when the step completes.
 * Returns `undefined` if icons is empty, undefined, or isActive is false.
 * Resets to the first icon when icons change or isActive becomes true.
 */
export function useCyclingIcon(icons: string[] | undefined, isActive: boolean): string | undefined {
	const [currentIndex, setCurrentIndex] = useState(0);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		if (!isActive || !icons || icons.length === 0) {
			if (intervalRef.current !== null) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			setCurrentIndex(0);
			return;
		}

		// Reset to first icon when icons change or isActive becomes true
		setCurrentIndex(0);

		intervalRef.current = setInterval(() => {
			setCurrentIndex((prev) => (prev + 1) % icons.length);
		}, CYCLING_INTERVAL_MS);

		return () => {
			if (intervalRef.current !== null) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [icons, isActive]);

	if (!isActive || !icons || icons.length === 0) {
		return undefined;
	}

	return icons[currentIndex];
}
